import React, { useState, useEffect } from 'react';
import { Shield, Key, Download, X, CheckCircle } from 'lucide-react';
import { API_URL } from '../../config';

export default function TwoFactorSetup({ onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1=QR, 2=Verify, 3=Backup
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    initTwoFactor();
  }, []);

  const initTwoFactor = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/2fa/init`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setQrCode(data.qr_code);
        setSecret(data.secret);
      } else {
        const data = await res.json();
        setError(data.detail || 'Fehler beim Laden');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('snapshot_token');
      const res = await fetch(`${API_URL}/api/auth/2fa/setup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ verification_code: verificationCode })
      });

      if (res.ok) {
        const data = await res.json();
        setBackupCodes(data.backup_codes);
        setStep(3);
      } else {
        const data = await res.json();
        setError(data.detail || 'Ungültiger Code');
      }
    } catch (err) {
      setError('Netzwerkfehler');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = `SNAPSHOT Backup-Codes\n\nBewahre diese Codes sicher auf!\nJeder Code kann nur einmal verwendet werden.\n\n${backupCodes.join('\n')}\n\nGeneriert: ${new Date().toLocaleString('de-DE')}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `snapshot-backup-codes-${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-black/95 to-black/90 border-2 border-[#10B981]/30 rounded-2xl p-8 max-w-md w-full relative">
        
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full border-2 border-white/20 text-white hover:bg-white/10 transition-all"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-8 h-8 text-[#10B981]" />
            <h2 className="text-2xl font-bold text-white">2FA aktivieren</h2>
          </div>
          <p className="text-white/60 text-sm">Schütze deinen Account mit Zwei-Faktor-Authentifizierung</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-6">
          <div className={`flex-1 h-2 rounded-full ${step >= 1 ? 'bg-[#10B981]' : 'bg-white/10'}`}></div>
          <div className={`flex-1 h-2 rounded-full ${step >= 2 ? 'bg-[#10B981]' : 'bg-white/10'}`}></div>
          <div className={`flex-1 h-2 rounded-full ${step >= 3 ? 'bg-[#10B981]' : 'bg-white/10'}`}></div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-500 text-sm">
            {error}
          </div>
        )}

        {/* Step 1: QR-Code */}
        {step === 1 && (
          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <>
                <div className="bg-white p-4 rounded-xl">
                  {qrCode ? (
                    <img src={qrCode} alt="QR Code" className="w-full" />
                  ) : (
                    <div className="w-full h-64 flex items-center justify-center">
                      <div className="w-12 h-12 border-4 border-[#10B981]/30 border-t-[#10B981] rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                
                <div className="p-3 bg-white/5 border border-white/20 rounded-lg">
                  <p className="text-white/60 text-xs mb-1">Oder manuell eingeben:</p>
                  <p className="text-[#10B981] font-mono text-sm break-all">{secret}</p>
                </div>
                
                <div className="text-white/70 text-sm space-y-2">
                  <p>1. Öffne deine Authenticator-App</p>
                  <p>2. Scanne den QR-Code</p>
                  <p>3. Gib den 6-stelligen Code ein</p>
                </div>
                
                <button
                  onClick={() => setStep(2)}
                  className="w-full py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-lg font-bold"
                >
                  Weiter
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 2: Verify */}
        {step === 2 && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div>
              <label className="block text-white/80 text-sm mb-2">6-stelliger Code aus App</label>
              <div className="relative">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]/50" />
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  maxLength={6}
                  className="w-full pl-12 pr-4 py-3 bg-black/50 border-2 border-[#10B981]/30 rounded-lg text-white text-center text-2xl tracking-widest focus:border-[#10B981] transition-all"
                  placeholder="123456"
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-lg font-bold disabled:opacity-50"
            >
              {loading ? 'Prüfe...' : '2FA aktivieren'}
            </button>
            
            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full text-white/60 hover:text-white text-sm"
            >
              ← Zurück
            </button>
          </form>
        )}

        {/* Step 3: Backup-Codes */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-[#10B981]" />
                <p className="text-[#10B981] font-bold">2FA erfolgreich aktiviert!</p>
              </div>
            </div>

            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <p className="text-orange-500 font-semibold text-sm mb-2">⚠️ Backup-Codes sichern!</p>
              <p className="text-white/60 text-xs">Diese Codes werden nur EINMAL angezeigt. Speichere sie sicher!</p>
            </div>

            <div className="bg-black/50 border border-white/20 rounded-lg p-4 max-h-48 overflow-y-auto">
              {backupCodes.map((code, index) => (
                <div key={index} className="font-mono text-[#10B981] text-sm py-1">
                  {code}
                </div>
              ))}
            </div>

            <button
              onClick={downloadBackupCodes}
              className="w-full py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-white font-semibold flex items-center justify-center gap-2"
            >
              <Download className="w-5 h-5" />
              Codes herunterladen
            </button>

            <button
              onClick={() => {
                onSuccess();
                onClose();
              }}
              className="w-full py-3 bg-gradient-to-r from-[#10B981] to-[#059669] text-black rounded-lg font-bold"
            >
              Fertig
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
